// src/pages/PrivacyPage.js
import React, { useEffect, useMemo, useState } from "react";

/**
 * ✅ SINGLE FILE (no restructure yet)
 * ✅ Auto language detection (query -> localStorage -> navigator)
 * ✅ Console logging for detection + switching
 * ✅ Top-right flag language switch (NL/DE)
 * ✅ NL brand name: UrenApp, DE brand name: StundenApp
 * ✅ Support email is OBFUSCATED (not present in HTML as plain text; assembled at runtime)
 * ✅ DE text aligned to DSGVO + BDSG and (website cookies) TTDSG reference
 *
 * IMPORTANT:
 * - Obfuscation reduces simple scraping, but nothing on a public webpage is 100% unsrappable.
 *   This implementation ensures the email is NOT in the HTML source as plain text or a mailto href.
 * - Fill in legal entity name/address if you want full compliance in DE (often expected).
 */

// ---------- Brand / Legal placeholders ----------
const BRAND = {
  nlAppName: "UrenApp",
  deAppName: "StundenApp",

  // Legal entity/controller display name (replace if you have a BV/UG/GmbH etc.)
  controllerName: "UrenApp", // TODO: set legal entity name if different
  controllerAddress: "—", // TODO: add postal address if you want full transparency (esp. DE)
  website: "urenapp.eu",

  // Not in HTML: built at runtime
  supportEmailParts: {
    user: "Support",
    domain: "urenapp.eu",
  },

  updatedNL: "Laatst bijgewerkt: 22 juni 2025",
  updatedDE: "Stand: 22. Juni 2025",
  euRegion: "EU/EER",
};

// ---------- Helpers: language detect/switch ----------
function getQueryLang() {
  try {
    const params = new URLSearchParams(window.location.search);
    const q = (params.get("lang") || "").toLowerCase();
    if (q === "nl" || q === "de") return q;
  } catch {}
  return null;
}

function setQueryLang(lang) {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("lang", lang);
    window.history.replaceState({}, "", url.toString());
  } catch {}
}

function detectLang() {
  const queryLang = getQueryLang();
  if (queryLang) return { lang: queryLang, source: "query" };

  try {
    const saved = (localStorage.getItem("privacy_lang") || "").toLowerCase();
    if (saved === "nl" || saved === "de") return { lang: saved, source: "localStorage" };
  } catch {}

  const nav = (navigator.language || navigator.userLanguage || "nl").toLowerCase();
  const lang = nav.startsWith("de") ? "de" : "nl";
  return { lang, source: "navigator" };
}

// ---------- Email obfuscation (no crawlable email in HTML) ----------
function buildSupportEmail() {
  const u = BRAND.supportEmailParts.user;
  const d = BRAND.supportEmailParts.domain;
  // This returns "Support@urenapp.eu" at runtime only.
  return `${u}@${d}`;
}

function openSupportMail(subject) {
  const email = buildSupportEmail();
  const s = subject ? `?subject=${encodeURIComponent(subject)}` : "";
  // Mailto is created only on click (not in static HTML)
  window.location.href = `mailto:${email}${s}`;
}

function copySupportEmailToClipboard() {
  const email = buildSupportEmail();
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(email).catch(() => {});
  } else {
    // fallback (no visible email)
    const ta = document.createElement("textarea");
    ta.value = email;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
    } catch {}
    document.body.removeChild(ta);
  }
}

// ---------- Content (NL/DE differ) ----------
function makeNLContent() {
  const appName = BRAND.nlAppName;

  return {
    title: `Privacyverklaring ${appName}`,
    intro: (
      <>
        <p style={{ fontSize: "1.08rem" }}>
          In deze privacyverklaring leggen wij uit hoe <strong>{appName}</strong> omgaat met
          persoonsgegevens. Wij verwerken persoonsgegevens zorgvuldig en in overeenstemming met de{" "}
          <strong>Algemene Verordening Gegevensbescherming (AVG)</strong> en andere toepasselijke
          privacywetgeving. Deze verklaring geldt voor het gebruik van onze app, website en
          aanverwante diensten (zoals support).
        </p>
        <p style={{ color: "#555" }}>
          Tip: je kunt rechtsboven altijd wisselen tussen Nederlands en Duits.
        </p>
      </>
    ),
    sections: [
      {
        h: "1. Verwerkingsverantwoordelijke",
        p: (
          <>
            <p>
              De verwerkingsverantwoordelijke is <strong>{BRAND.controllerName}</strong> ({appName}).
            </p>
            <ul>
              <li>Website: {BRAND.website}</li>
              <li>Adres: {BRAND.controllerAddress}</li>
              <li>
                Contact (support):{" "}
                <SupportContactNL />
              </li>
            </ul>
          </>
        ),
      },
      {
        h: "2. Welke persoonsgegevens verwerken wij?",
        p: (
          <>
            <p>
              Wij verwerken uitsluitend gegevens die noodzakelijk zijn om de dienst te leveren, te
              beveiligen en te verbeteren. Dit kan omvatten:
            </p>
            <ul>
              <li>
                <strong>Accountgegevens</strong>: e-mailadres, naam (indien door jou opgegeven),
                accountinstellingen en (gehashte) authenticatiegegevens.
              </li>
              <li>
                <strong>Inhoud die jij invoert</strong>: geregistreerde uren/diensten, notities,
                rapportages en instellingen.
              </li>
              <li>
                <strong>Supportgegevens</strong>: inhoud van supportverzoeken en bijlagen (zoals
                screenshots) wanneer je die instuurt.
              </li>
              <li>
                <strong>Technische gegevens</strong>: IP-adres, app-versie, besturingssysteem,
                taalinstelling, crash-/loggegevens (doelgebonden).
              </li>
              <li>
                <strong>Abonnements-/aankoopstatus</strong> (indien van toepassing): status
                (bijv. actief/expired), platform (Apple/Google) en technische identifiers voor
                verificatie. Betaalkaartgegevens ontvangen wij niet; betaling loopt via Apple/Google.
              </li>
            </ul>
            <p style={{ color: "#555" }}>
              {appName} is niet bedoeld voor kinderen onder de 16 jaar. Wij verzamelen niet bewust
              gegevens van kinderen.
            </p>
          </>
        ),
      },
      {
        h: "3. Doeleinden en rechtsgronden (AVG art. 6)",
        p: (
          <>
            <p>Wij verwerken persoonsgegevens uitsluitend voor de volgende doelen:</p>
            <ul>
              <li>
                <strong>Uitvoering van de overeenkomst</strong>: account aanmaken, uren registreren,
                overzichten/rapportages tonen, synchronisatie en kernfunctionaliteit.
              </li>
              <li>
                <strong>Beveiliging en fraudepreventie</strong> (gerechtvaardigd belang): bescherming
                tegen misbruik, ongeautoriseerde toegang en aanvallen.
              </li>
              <li>
                <strong>Ondersteuning</strong>: support, probleemoplossing en communicatie.
              </li>
              <li>
                <strong>Verbetering</strong> (gerechtvaardigd belang): analyses op basis van
                geaggregeerde/technische signalen om stabiliteit en kwaliteit te verbeteren.
              </li>
              <li>
                <strong>Wettelijke verplichtingen</strong>: wanneer wij daartoe verplicht zijn.
              </li>
              <li>
                <strong>Toestemming</strong>: waar vereist (bijv. bepaalde cookies/tracking of
                gepersonaliseerde advertenties).
              </li>
            </ul>
          </>
        ),
      },
      {
        h: "4. Bewaartermijnen",
        p: (
          <>
            <p>
              Wij bewaren persoonsgegevens niet langer dan noodzakelijk voor de doeleinden waarvoor
              zij worden verwerkt, tenzij een langere bewaartermijn wettelijk is vereist.
            </p>
            <ul>
              <li>
                Account- en ingevoerde gegevens: zolang je account actief is; na verwijdering wissen
                of anonimiseren wij gegevens binnen redelijke termijn (tenzij wettelijke plicht).
              </li>
              <li>
                Support- en loggegevens: zo kort mogelijk en doelgebonden; daarna verwijdering of
                aggregatie.
              </li>
            </ul>
          </>
        ),
      },
      {
        h: "5. Delen met derden (verwerkers) en doorgiften",
        p: (
          <>
            <p>
              Wij delen gegevens uitsluitend met partijen die nodig zijn voor onze dienstverlening
              (bijv. hosting, e-mail, app store verificatie). Wij verkopen jouw gegevens nooit.
            </p>
            <p style={{ color: "#555" }}>
              Indien gegevens buiten de {BRAND.euRegion} worden verwerkt, zorgen wij voor passende
              waarborgen (bijv. adequaatheidsbesluit of Standard Contractual Clauses).
            </p>
          </>
        ),
      },
      {
        h: "6. Beveiliging",
        p: (
          <>
            <p>
              Wij nemen passende technische en organisatorische maatregelen, waaronder
              versleutelde verbindingen (HTTPS/TLS), toegangscontrole, monitoring en updates.
            </p>
          </>
        ),
      },
      {
        h: "7. Jouw rechten",
        p: (
          <>
            <p>Je hebt onder de AVG onder andere de volgende rechten:</p>
            <ul>
              <li>Inzage</li>
              <li>Rectificatie</li>
              <li>Verwijdering</li>
              <li>Beperking van verwerking</li>
              <li>Dataportabiliteit</li>
              <li>Bezwaar</li>
              <li>Intrekken van toestemming (indien van toepassing)</li>
            </ul>
            <p>
              Verzoeken kun je indienen via <SupportContactNL subject={`Privacyverzoek (${appName})`} />.
              Ter bescherming kunnen wij om proportionele identiteitsverificatie vragen.
            </p>
          </>
        ),
      },
      {
        h: "8. Klachten",
        p: (
          <>
            <p>
              Ben je niet tevreden over hoe wij met jouw gegevens omgaan? Neem contact op via{" "}
              <SupportContactNL subject={`Privacyvraag (${appName})`} />. Je hebt ook het recht om een
              klacht in te dienen bij de Autoriteit Persoonsgegevens:{" "}
              <a
                href="https://autoriteitpersoonsgegevens.nl/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#2247d5" }}
              >
                autoriteitpersoonsgegevens.nl
              </a>
              .
            </p>
          </>
        ),
      },
      {
        h: "9. Wijzigingen",
        p: (
          <>
            <p>
              Wij kunnen deze privacyverklaring aanpassen bij wijzigingen in onze dienstverlening of
              wetgeving. De actuele versie staat altijd op deze pagina.
            </p>
          </>
        ),
      },
    ],
    updated: BRAND.updatedNL,
  };
}

function makeDEContent() {
  const appName = BRAND.deAppName;

  return {
    title: `Datenschutzerklärung ${appName}`,
    intro: (
      <>
        <p style={{ fontSize: "1.08rem" }}>
          In dieser Datenschutzerklärung erläutern wir, wie <strong>{appName}</strong> personenbezogene
          Daten verarbeitet. Die Verarbeitung erfolgt gemäß der{" "}
          <strong>Datenschutz-Grundverordnung (DSGVO)</strong> sowie – soweit anwendbar – ergänzend nach
          dem <strong>BDSG</strong>. Für Cookies/ähnliche Technologien auf der Website können zusätzlich
          die Vorgaben des <strong>TTDSG</strong> relevant sein.
        </p>
        <p style={{ color: "#555" }}>
          Hinweis: Sie können oben rechts jederzeit zwischen Deutsch und Niederländisch wechseln.
        </p>
      </>
    ),
    sections: [
      {
        h: "1. Verantwortlicher",
        p: (
          <>
            <p>
              Verantwortlicher im Sinne der DSGVO ist <strong>{BRAND.controllerName}</strong> ({appName}).
            </p>
            <ul>
              <li>Website: {BRAND.website}</li>
              <li>Adresse: {BRAND.controllerAddress}</li>
              <li>Kontakt (Support): <SupportContactDE /></li>
            </ul>
            <p style={{ color: "#555" }}>
              In Deutschland wird häufig außerdem ein Impressum erwartet. Falls vorhanden, stellen wir
              dieses auf der Website bereit.
            </p>
          </>
        ),
      },
      {
        h: "2. Welche Daten verarbeiten wir?",
        p: (
          <>
            <p>Wir verarbeiten nur Daten, die für Betrieb, Sicherheit und Verbesserung erforderlich sind:</p>
            <ul>
              <li>
                <strong>Kontodaten</strong>: E-Mail-Adresse, Name (falls angegeben), Einstellungen und
                (gehashte) Authentifizierungsdaten.
              </li>
              <li>
                <strong>Eingaben in der App</strong>: Arbeitszeiten/Schichten, Notizen, Berichte, Einstellungen.
              </li>
              <li>
                <strong>Supportdaten</strong>: Inhalte von Supportanfragen und Anhänge (z. B. Screenshots),
                sofern Sie diese übermitteln.
              </li>
              <li>
                <strong>Technische Daten</strong>: IP-Adresse, App-Version, Betriebssystem, Spracheinstellung,
                Diagnose-/Logdaten (zweckgebunden).
              </li>
              <li>
                <strong>Abonnement-/Kaufstatus</strong> (falls zutreffend): Status (z. B. aktiv/abgelaufen),
                Plattform (Apple/Google) und technische Kennungen zur Verifizierung. Zahlungsdaten (Karte)
                erhalten wir nicht; Abwicklung über Apple/Google.
              </li>
            </ul>
            <p style={{ color: "#555" }}>
              {appName} richtet sich nicht an Kinder unter 16 Jahren. Wir erheben Daten von Kindern nicht wissentlich.
            </p>
          </>
        ),
      },
      {
        h: "3. Zwecke und Rechtsgrundlagen (Art. 6 DSGVO)",
        p: (
          <>
            <p>Die Verarbeitung erfolgt insbesondere zu folgenden Zwecken und Rechtsgrundlagen:</p>
            <ul>
              <li>
                <strong>Vertragserfüllung</strong> (Art. 6 Abs. 1 lit. b DSGVO): Konto, Zeiterfassung,
                Auswertungen, Synchronisierung, Kernfunktionen.
              </li>
              <li>
                <strong>Berechtigte Interessen</strong> (Art. 6 Abs. 1 lit. f DSGVO): Sicherheit, Missbrauchs-
                und Betrugsprävention, Stabilität, Qualitätssicherung.
              </li>
              <li>
                <strong>Rechtliche Verpflichtungen</strong> (Art. 6 Abs. 1 lit. c DSGVO): soweit anwendbar.
              </li>
              <li>
                <strong>Einwilligung</strong> (Art. 6 Abs. 1 lit. a DSGVO): sofern erforderlich (z. B. bestimmte
                Tracking-/Marketing-Technologien oder personalisierte Werbung).
              </li>
            </ul>
            <p style={{ color: "#555" }}>
              Bei „berechtigtem Interesse“ nehmen wir eine Interessenabwägung vor und setzen Schutzmaßnahmen
              wie Datenminimierung und begrenzte Speicherfristen ein.
            </p>
          </>
        ),
      },
      {
        h: "4. Speicherdauer",
        p: (
          <>
            <p>
              Wir speichern Daten nur so lange, wie es für die genannten Zwecke erforderlich ist oder
              gesetzliche Aufbewahrungspflichten bestehen.
            </p>
            <ul>
              <li>
                Konto- und App-Daten: solange das Konto aktiv ist; nach Löschung löschen oder anonymisieren wir
                Daten innerhalb angemessener Frist (sofern keine Pflichten entgegenstehen).
              </li>
              <li>
                Support- und Logdaten: so kurz wie möglich und zweckgebunden; danach Löschung oder Aggregation.
              </li>
            </ul>
          </>
        ),
      },
      {
        h: "5. Empfänger, Auftragsverarbeiter, Drittlandübermittlungen",
        p: (
          <>
            <p>
              Wir geben Daten nur weiter, wenn dies zur Bereitstellung der Dienste erforderlich ist (z. B. Hosting,
              E-Mail-Dienstleister, App-Store-Verifizierung) oder wir gesetzlich verpflichtet sind. Kein Verkauf von Daten.
            </p>
            <p style={{ color: "#555" }}>
              Werden Daten außerhalb der {BRAND.euRegion} verarbeitet, stellen wir geeignete Garantien sicher
              (z. B. Angemessenheitsbeschluss oder Standardvertragsklauseln).
            </p>
          </>
        ),
      },
      {
        h: "6. Cookies/Website (TTDSG)",
        p: (
          <>
            <p>
              Auf der Website können Cookies/ähnliche Technologien für Funktion, Sicherheit und – sofern aktiviert –
              Statistik/Marketing eingesetzt werden. Soweit erforderlich holen wir Ihre Einwilligung ein.
            </p>
            <p>
              Details finden Sie in unserer{" "}
              <a href="/cookies" style={{ color: "#2247d5" }}>
                Cookie-Richtlinie
              </a>
              .
            </p>
          </>
        ),
      },
      {
        h: "7. Ihre Rechte",
        p: (
          <>
            <p>Sie haben insbesondere folgende Rechte nach DSGVO:</p>
            <ul>
              <li>Auskunft</li>
              <li>Berichtigung</li>
              <li>Löschung</li>
              <li>Einschränkung der Verarbeitung</li>
              <li>Datenübertragbarkeit</li>
              <li>Widerspruch</li>
              <li>Widerruf erteilter Einwilligungen (falls zutreffend)</li>
            </ul>
            <p>
              Anfragen richten Sie bitte an <SupportContactDE subject={`Datenschutzanfrage (${appName})`} />.
              Zur Missbrauchsvermeidung können wir eine verhältnismäßige Identitätsprüfung verlangen.
            </p>
          </>
        ),
      },
      {
        h: "8. Beschwerden / Aufsichtsbehörde",
        p: (
          <>
            <p>
              Wenn Sie mit der Verarbeitung nicht einverstanden sind, kontaktieren Sie uns bitte zuerst unter{" "}
              <SupportContactDE subject={`Datenschutzfrage (${appName})`} />.
            </p>
            <p style={{ color: "#555" }}>
              Zudem besteht ein Beschwerderecht bei einer Datenschutzaufsichtsbehörde. Zuständig ist in der Regel die
              Aufsichtsbehörde Ihres Bundeslandes oder Ihres gewöhnlichen Aufenthalts.
            </p>
          </>
        ),
      },
      {
        h: "9. Änderungen",
        p: (
          <>
            <p>
              Wir können diese Datenschutzerklärung anpassen, z. B. bei Änderungen unserer Dienste oder der Rechtslage.
              Die aktuelle Version ist stets auf dieser Seite verfügbar.
            </p>
          </>
        ),
      },
    ],
    updated: BRAND.updatedDE,
  };
}

function SupportContactNL({ subject }) {
  // No crawlable email: render as buttons/text without the address in markup.
  return (
    <span>
      <button
        type="button"
        onClick={() => openSupportMail(subject || `Privacy (${BRAND.nlAppName})`)}
        style={linkButtonStyle}
        title="Stuur e-mail naar support"
      >
        e-mail support
      </button>
      <span style={{ color: "#888" }}> · </span>
      <button
        type="button"
        onClick={() => copySupportEmailToClipboard()}
        style={linkButtonStyle}
        title="Kopieer e-mailadres"
      >
        kopieer adres
      </button>
    </span>
  );
}

function SupportContactDE({ subject }) {
  return (
    <span>
      <button
        type="button"
        onClick={() => openSupportMail(subject || `Datenschutz (${BRAND.deAppName})`)}
        style={linkButtonStyle}
        title="E-Mail an Support senden"
      >
        Support per E-Mail
      </button>
      <span style={{ color: "#888" }}> · </span>
      <button
        type="button"
        onClick={() => copySupportEmailToClipboard()}
        style={linkButtonStyle}
        title="E-Mail-Adresse kopieren"
      >
        Adresse kopieren
      </button>
    </span>
  );
}

const linkButtonStyle = {
  background: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  color: "#2247d5",
  cursor: "pointer",
  fontWeight: 800,
  textDecoration: "underline",
  fontSize: "inherit",
};

// ---------- Page ----------
export default function PrivacyPage() {
  const [{ lang, source }, setLangState] = useState(() => detectLang());

  useEffect(() => {
    const nav = (navigator.language || navigator.userLanguage || "").toLowerCase();
    const q = getQueryLang();
    let saved = null;
    try {
      saved = (localStorage.getItem("privacy_lang") || "").toLowerCase();
    } catch {}

    // eslint-disable-next-line no-console
    console.log("[PrivacyPage] language detect:", {
      selected: lang,
      source,
      navigatorLanguage: nav || null,
      queryLang: q,
      savedLang: saved || null,
      appName: lang === "de" ? BRAND.deAppName : BRAND.nlAppName,
    });
  }, [lang, source]);

  const content = useMemo(() => {
    return lang === "de" ? makeDEContent() : makeNLContent();
  }, [lang]);

  const switchTo = (nextLang) => {
    if (nextLang === lang) return;

    // eslint-disable-next-line no-console
    console.log("[PrivacyPage] language switch:", {
      from: lang,
      to: nextLang,
      appName: nextLang === "de" ? BRAND.deAppName : BRAND.nlAppName,
    });

    try {
      localStorage.setItem("privacy_lang", nextLang);
    } catch {}

    setQueryLang(nextLang);
    setLangState({ lang: nextLang, source: "manual" });
  };

  const cardStyle = {
    position: "relative",
    maxWidth: 760,
    margin: "4rem auto",
    padding: "2.8rem 1.3rem",
    background: "#fff",
    borderRadius: 18,
    boxShadow: "0 2px 32px 0 rgba(31,38,135,0.11)",
  };

  const switcherStyle = {
    position: "absolute",
    top: 18,
    right: 18,
    display: "flex",
    gap: 8,
    alignItems: "center",
    userSelect: "none",
  };

  const langBtn = (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    borderRadius: 999,
    border: `1px solid ${active ? "#2247d5" : "rgba(0,0,0,0.12)"}`,
    background: active ? "rgba(34,71,213,0.08)" : "#fff",
    cursor: active ? "default" : "pointer",
    fontWeight: 900,
    fontSize: 13,
    color: active ? "#2247d5" : "#222",
    boxShadow: active ? "0 0 0 2px rgba(34,71,213,0.10) inset" : "none",
  });

  const h1Style = { color: "#2247d5", marginBottom: 18, fontWeight: 900 };
  const h3Style = { color: "#4b8cf6", marginTop: 34 };

  return (
    <div style={cardStyle}>
      {/* Language switcher (top-right flags) */}
      <div style={switcherStyle} aria-label="Language switcher">
        <button
          type="button"
          onClick={() => switchTo("nl")}
          style={langBtn(lang === "nl")}
          title="Nederlands"
          aria-pressed={lang === "nl"}
        >
          <span style={{ fontSize: 16 }}>🇳🇱</span> NL
        </button>
        <button
          type="button"
          onClick={() => switchTo("de")}
          style={langBtn(lang === "de")}
          title="Deutsch"
          aria-pressed={lang === "de"}
        >
          <span style={{ fontSize: 16 }}>🇩🇪</span> DE
        </button>
      </div>

      <h1 style={h1Style}>{content.title}</h1>

      {content.intro}

      {content.sections.map((s, idx) => (
        <div key={idx}>
          <h3 style={h3Style}>{s.h}</h3>
          {s.p}
        </div>
      ))}

      <p style={{ marginTop: 36, color: "#888", fontSize: "0.97rem" }}>
        {content.updated}
      </p>
    </div>
  );
}
